import { graphql } from '@eluve/graphql.tada';

// Fragment for experiment variant basic data
export const experimentVariantFragment = graphql(`
  fragment ExperimentVariant on ExperimentVariants @_unmask {
    __typename
    id
    experimentId
    name
    posthogVariantKey
    variantConfig
    createdAt
    updatedAt
  }
`);

// Fragment for experiment basic data (without variants)
export const experimentBaseFragment = graphql(`
  fragment ExperimentBase on Experiments @_unmask {
    __typename
    id
    name
    description
    posthogFlagKey
    timezone
    startTime
    endTime
    isActive
    firstActivatedAt
    rank
    mode
    testingOverrides
    createdAt
    updatedAt
  }
`);

// Fragment for experiment with variants
export const experimentWithVariantsFragment = graphql(
  `
    fragment ExperimentWithVariants on Experiments @_unmask {
      ...ExperimentBase
      variants(orderBy: { createdAt: ASC }) {
        ...ExperimentVariant
      }
    }
  `,
  [experimentBaseFragment, experimentVariantFragment],
);

// Query to get all experiments with their variants
export const getExperimentsQuery = graphql(
  `
    query getExperiments($orderBy: [ExperimentsOrderBy!]) {
      experiments(orderBy: $orderBy) {
        ...ExperimentWithVariants
      }
    }
  `,
  [experimentWithVariantsFragment],
);

// Query to get a single experiment by ID
export const getExperimentByIdQuery = graphql(
  `
    query getExperimentById($experimentId: uuid!) {
      experimentsByPk(id: $experimentId) {
        ...ExperimentWithVariants
      }
    }
  `,
  [experimentWithVariantsFragment],
);

// Mutation to create a new experiment
export const createExperimentMutation = graphql(
  `
    mutation createExperiment(
      $name: String!
      $description: String
      $posthogFlagKey: String!
      $timezone: String
      $startTime: timestamptz
      $endTime: timestamptz
      $rank: Int
      $mode: ExperimentModeTypesEnum
    ) {
      insertExperimentsOne(
        object: {
          name: $name
          description: $description
          posthogFlagKey: $posthogFlagKey
          timezone: $timezone
          startTime: $startTime
          endTime: $endTime
          rank: $rank
          mode: $mode
        }
      ) {
        ...ExperimentWithVariants
      }
    }
  `,
  [experimentWithVariantsFragment],
);

// Mutation to update an experiment
export const updateExperimentMutation = graphql(
  `
    mutation updateExperiment(
      $experimentId: uuid!
      $name: String
      $description: String
      $posthogFlagKey: String
      $timezone: String
      $startTime: timestamptz
      $endTime: timestamptz
      $rank: Int
      $mode: ExperimentModeTypesEnum
    ) {
      updateExperimentsByPk(
        pkColumns: { id: $experimentId }
        _set: {
          name: $name
          description: $description
          posthogFlagKey: $posthogFlagKey
          timezone: $timezone
          startTime: $startTime
          endTime: $endTime
          rank: $rank
          mode: $mode
        }
      ) {
        ...ExperimentWithVariants
      }
    }
  `,
  [experimentWithVariantsFragment],
);

// Mutation to delete an experiment
export const deleteExperimentMutation = graphql(`
  mutation deleteExperiment($experimentId: uuid!) {
    deleteExperimentsByPk(id: $experimentId) {
      __typename
      id
    }
  }
`);

// Mutation to create a new experiment variant
export const createExperimentVariantMutation = graphql(
  `
    mutation createExperimentVariant(
      $experimentId: uuid!
      $name: String!
      $posthogVariantKey: String!
      $variantConfig: jsonb
    ) {
      insertExperimentVariantsOne(
        object: {
          experimentId: $experimentId
          name: $name
          posthogVariantKey: $posthogVariantKey
          variantConfig: $variantConfig
        }
      ) {
        ...ExperimentVariant
      }
    }
  `,
  [experimentVariantFragment],
);

// Mutation to update an experiment variant
export const updateExperimentVariantMutation = graphql(
  `
    mutation updateExperimentVariant(
      $variantId: uuid!
      $name: String
      $variantConfig: jsonb
    ) {
      updateExperimentVariantsByPk(
        pkColumns: { id: $variantId }
        _set: { name: $name, variantConfig: $variantConfig }
      ) {
        ...ExperimentVariant
      }
    }
  `,
  [experimentVariantFragment],
);

// Mutation to delete an experiment variant
export const deleteExperimentVariantMutation = graphql(`
  mutation deleteExperimentVariant($variantId: uuid!) {
    deleteExperimentVariantsByPk(id: $variantId) {
      __typename
      id
      experimentId
    }
  }
`);

// Mutation to toggle experiment active status
export const setExperimentStatusMutation = graphql(`
  mutation setExperimentStatus($experimentId: uuid!, $isActive: Boolean!) {
    updateExperimentsByPk(
      pkColumns: { id: $experimentId }
      _set: { isActive: $isActive }
    ) {
      __typename
      id
      isActive
      firstActivatedAt
    }
  }
`);

// Query for prompt templates (for override selections)
export const getPromptTemplatesForOverrideQuery = graphql(`
  query getPromptTemplatesForOverride {
    promptTemplates(orderBy: { name: ASC }) {
      __typename
      id
      name
      outputType
      prompt_template_variants(orderBy: { variantNumber: DESC }) {
        __typename
        promptTemplateId
        variantNumber
        isCurrentDefault
      }
    }
  }
`);

// Query for output templates (for override selections)
export const getOutputTemplatesForOverrideQuery = graphql(`
  query getOutputTemplatesForOverride {
    llmOutputTemplates(
      where: { isActive: { _eq: true }, tenantId: { _isNull: true } }
      orderBy: { name: ASC }
    ) {
      __typename
      id
      name
      llmOutputType
      variants(orderBy: { variantNumber: DESC }) {
        __typename
        outputTemplateId
        variantNumber
        isCurrentDefault
      }
    }
  }
`);

// Query for LLM model args (for override selections)
export const getLlmModelArgsForOverrideQuery = graphql(`
  query getLlmModelArgsForOverride {
    llmModelArgs(
      where: { isActive: { _eq: true } }
      orderBy: { modelType: ASC }
    ) {
      __typename
      id
      name
      modelType
      args
    }
  }
`);

// Query for eluve-admin users (for testing overrides)
export const getEluveAdminUsersQuery = graphql(`
  query getEluveAdminUsers {
    users(where: { isEluveAdmin: { _eq: true } }, orderBy: { email: ASC }) {
      __typename
      id
      email
      firstName
      lastName
    }
  }
`);

// Mutation to update testing overrides
export const updateTestingOverridesMutation = graphql(`
  mutation updateTestingOverrides(
    $experimentId: uuid!
    $testingOverrides: jsonb!
  ) {
    updateExperimentsByPk(
      pkColumns: { id: $experimentId }
      _set: { testingOverrides: $testingOverrides }
    ) {
      __typename
      id
      testingOverrides
    }
  }
`);
